import {
  EventDetails,
  EventWithCustomField,
  TicketTypeItemType,
} from "@ac-expo/api-types";
import { createPartialCompleteBuyableItem } from "@ac-expo/utils";
import { useCallback, useMemo } from "react";
import { BuyableItemWithPricing } from "./useLocalCart";

export type EntriesType = "ticket" | "badge" | "activity";

export const useEventAvailableEntries = ({
  event,
  type = ["ticket", "badge", "activity"],
  isProfessional,
  additionalUserTypes = [],
}: {
  event: EventWithCustomField<EventDetails> | undefined;
  type?: EntriesType[];
  isProfessional: boolean;
  additionalUserTypes?: string[];
}) => {
  const isEntryAvailable = useCallback(
    (item: Pick<TicketTypeItemType, "userType">) =>
      !!isProfessional ||
      item.userType === "visitor" ||
      item.userType === "all" ||
      (item.userType !== null && additionalUserTypes.includes(item.userType)),
    [isProfessional, additionalUserTypes]
  );

  const [ticketTypes, activityTicketTypes] = useMemo(
    () =>
      event?.ticketTypes.reduce(
        (acc, ticketType) => {
          if (ticketType.activityId === null) {
            acc[0].push(ticketType);
          } else {
            acc[1].push(ticketType);
          }
          return acc;
        },
        [[], []] as EventWithCustomField<EventDetails>["ticketTypes"][]
      ) ?? [[], []],
    [event]
  );

  const badgeTypes = useMemo(
    () =>
      event?.badgeTypes.filter(
        (badge) => event?.areBadgesOnline || badge.userType !== "visitor"
      ) ?? [],
    [event]
  );

  const {
    availableTicketTypes,
    availableBadgeTypes,
    availableActivityTicketTypes,
  } = useMemo(
    () => ({
      availableTicketTypes: ticketTypes.filter(isEntryAvailable),
      availableBadgeTypes: badgeTypes.filter(isEntryAvailable),
      availableActivityTicketTypes:
        activityTicketTypes.filter(isEntryAvailable),
    }),
    [activityTicketTypes, badgeTypes, isEntryAvailable, ticketTypes]
  );

  const eventData = useMemo(
    () =>
      event
        ? {
            id: event.id,
            name: event.name,
          }
        : undefined,
    [event],
  );

  const availableEntries: BuyableItemWithPricing[] = useMemo(() => {
    const badgesBuyableItems = type.includes("badge")
      ? availableBadgeTypes.map((badgeType) => ({
          buyableItem: createPartialCompleteBuyableItem({
            buyableType: "badge",
            badgeType: {
              id: badgeType.id,
              name: badgeType.name,
              quantityLeft: badgeType.quantityLeft,
              groupMinSize: badgeType.groupMinSize,
              event: eventData,
            },
          }),
          pricing: badgeType.pricing,
        }))
      : [];
    const ticketsBuyableItems = type.includes("ticket")
      ? availableTicketTypes.map((ticketType) => ({
          buyableItem: createPartialCompleteBuyableItem({
            buyableType: "ticket",
            ticketType: {
              id: ticketType.id,
              name: ticketType.name,
              quantityLeft: ticketType.quantityLeft,
              groupMinSize: ticketType.groupMinSize,
              userType: ticketType.userType,
              event: eventData,
            },
          }),
          pricing: ticketType.pricing,
        }))
      : [];
    const activityTicketsBuyableItems = type.includes("activity")
      ? availableActivityTicketTypes.map((ticketType) => ({
          buyableItem: createPartialCompleteBuyableItem({
            buyableType: "ticket",
            ticketType: {
              id: ticketType.id,
              name: ticketType.name,
              quantityLeft: ticketType.quantityLeft,
              groupMinSize: ticketType.groupMinSize,
              userType: ticketType.userType,
              event: eventData,
            },
          }),
          pricing: ticketType.pricing,
        }))
      : [];
    return [
      ...badgesBuyableItems,
      ...ticketsBuyableItems,
      ...activityTicketsBuyableItems,
    ];
  }, [
    availableBadgeTypes,
    availableTicketTypes,
    availableActivityTicketTypes,
    type,
    eventData,
  ]);

  return {
    ticketTypes,
    activityTicketTypes,
    badgeTypes,
    availableEntries,
    availableTicketTypes,
    availableBadgeTypes,
    availableActivityTicketTypes,
  };
};
